import { useEffect, useState } from 'react'
import { getUsers, type User } from '@/api/users'
import { getMemberships, updateMembership, expireOverdueMemberships, type Membership } from '@/api/memberships'
import { StatusBadge } from '@/components/shared/StatusBadge'
import { MembershipForm } from './MembershipForm'
import { AlertDialog, AlertDialogContent, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from '@/components/ui/alert-dialog'
import { toast } from 'sonner'
import { ApiError } from '@/lib/api-error'

export function MembershipList() {
  const [users, setUsers] = useState<User[]>([])
  const [memberships, setMemberships] = useState<Membership[]>([])
  const [selectedUser, setSelectedUser] = useState<string>('')
  const [loading, setLoading] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [membershipMode, setMembershipMode] = useState<'create' | 'renew' | 'edit'>('create')
  const [editingMembership, setEditingMembership] = useState<Membership | null>(null)
  const [pendingCancel, setPendingCancel] = useState<string | null>(null)

  useEffect(() => {
    getUsers().then((r: any) => setUsers(r.users)).catch(() => toast.error('Error al cargar miembros'))
  }, [])

  useEffect(() => {
    if (!selectedUser) return
    setLoading(true)
    getMemberships(selectedUser)
      .then((r: any) => setMemberships(r.memberships))
      .catch((e: any) => toast.error(e instanceof ApiError ? e.message : 'Error'))
      .finally(() => setLoading(false))
  }, [selectedUser])

  function openCreate() {
    setMembershipMode('create')
    setEditingMembership(null)
    setDialogOpen(true)
  }

  function openEdit(membership: Membership) {
    setMembershipMode('edit')
    setEditingMembership(membership)
    setDialogOpen(true)
  }

  function openRenew(membership: Membership) {
    setMembershipMode('renew')
    setEditingMembership(membership)
    setDialogOpen(true)
  }

  function handleMembershipSaved(saved: Membership) {
    setMemberships((prev) => {
      const idx = prev.findIndex((m) => m.id === saved.id)
      if (idx >= 0) {
        const next = [...prev]
        next[idx] = saved
        return next
      }
      return [...prev, saved]
    })
  }

  async function handleExpire() {
    try {
      const r = await expireOverdueMemberships()
      toast.success(`${r.expired} membresías expiradas`)
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Error')
    }
  }

  async function handleCancel(id: string) {
    try {
      await updateMembership(id, { status: 'cancelled' })
      setMemberships((prev) => prev.map((m) => m.id === id ? { ...m, status: 'cancelled' } : m))
      toast.success('Membresía cancelada')
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Error')
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h1 className="text-text-primary font-semibold text-lg">Membresías</h1>
        <div className="flex items-center gap-2">
          <button
            onClick={openCreate}
            className="bg-accent text-white text-xs px-3 py-1.5 rounded-md hover:bg-accent/90 transition-colors"
          >
            + Nueva membresía
          </button>
          <button onClick={handleExpire} className="border border-border text-text-muted text-xs px-3 py-1.5 rounded-md hover:text-text-primary transition-colors">
            Expirar vencidas
          </button>
        </div>
      </div>

      <select
        value={selectedUser}
        onChange={(e) => setSelectedUser(e.target.value)}
        className="bg-surface border border-border rounded-md px-3 py-2 text-sm text-text-primary max-w-xs focus:outline-none focus:ring-1 focus:ring-accent"
      >
        <option value="">Seleccionar miembro...</option>
        {users.map((u) => (
          <option key={u.id} value={u.id}>{u.name} — CC {u.employee_no}</option>
        ))}
      </select>

      {loading && <p className="text-text-muted text-sm">Cargando...</p>}

      {memberships.length > 0 && (
        <div className="bg-surface rounded-lg border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left px-4 py-2.5 text-text-muted text-xs">Estado</th>
                <th className="text-left px-4 py-2.5 text-text-muted text-xs">Inicio</th>
                <th className="text-left px-4 py-2.5 text-text-muted text-xs">Vencimiento</th>
                <th className="text-right px-4 py-2.5 text-text-muted text-xs">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {memberships.map((m) => (
                <tr key={m.id} className="border-b border-border last:border-0">
                  <td className="px-4 py-3"><StatusBadge status={m.status} /></td>
                  <td className="px-4 py-3 text-text-muted">{String(m.start_date).split('T')[0]}</td>
                  <td className="px-4 py-3 text-text-muted">{String(m.end_date).split('T')[0]}</td>
                  <td className="px-4 py-3 text-right flex items-center justify-end gap-3">
                    <button onClick={() => openEdit(m)} className="text-accent text-xs hover:underline">
                      Editar
                    </button>
                    {m.status === 'expired' && (
                      <button onClick={() => openRenew(m)} className="text-accent text-xs hover:underline">
                        Renovar
                      </button>
                    )}
                    {m.status === 'active' && (
                      <button onClick={() => setPendingCancel(m.id)} className="text-red-600 dark:text-red-400 hover:text-red-500 dark:hover:text-red-300 text-xs transition-colors">
                        Cancelar
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <MembershipForm
        open={dialogOpen}
        mode={membershipMode}
        userId={membershipMode === 'create' ? undefined : editingMembership?.user_id}
        membership={membershipMode === 'edit' || membershipMode === 'renew' ? editingMembership : null}
        onClose={() => setDialogOpen(false)}
        onSaved={handleMembershipSaved}
      />

      <AlertDialog open={!!pendingCancel} onOpenChange={() => setPendingCancel(null)}>
        <AlertDialogContent>
          <AlertDialogTitle>¿Cancelar membresía?</AlertDialogTitle>
          <AlertDialogDescription>El miembro perderá acceso al vencer el período actual.</AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => { handleCancel(pendingCancel!); setPendingCancel(null) }}>
              Cancelar membresía
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
